import { createEntity, fetchEntities, upsertEntity } from 'actions/entities'
import React from 'react'
import { connect } from 'react-redux'
import { findEntity } from 'selectors/entities'
import { Button, Card, Dropdown } from 'semantic-ui-react'
import CollapsableCard from '../notifications/CollapsableCard.jsx'
import { getPersistedEntities } from 'selectors/entities'

const MAX_QUICK_OPTIONS = 4

class MatchProposalListItem extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      showDropdowns: false,
      selectedDossierId: null,
      selectedConsultationTypeId: null
    }
  }

  componentDidMount() {
    if (!this.props.allDossiers || this.props.allDossiers.length === 0) {
      this.props.loadDossiers()
    }
  }

  buildQuickOptions() {
    const { candidateDossiers, candidateConsultationTypes } = this.props
    const dossiers = candidateDossiers.length > 0 ? candidateDossiers : [null]
    const types = candidateConsultationTypes.length > 0 ? candidateConsultationTypes : [null]

    const options = []
    for (const dossier of dossiers) {
      for (const ct of types) {
        options.push({ dossier, consultationType: ct })
      }
    }
    return options
  }

  formatOptionLabel(option) {
    const parts = []
    if (option.dossier && option.dossier.patient) {
      parts.push(`${option.dossier.patient.firstName} ${option.dossier.patient.lastName}`)
    }
    if (option.consultationType) {
      parts.push(option.consultationType.name)
    }
    return parts.join(' - ') || '?'
  }

  handleQuickOption(option) {
    this.props.resolveProposal(
      this.props.proposal.id,
      option.dossier ? option.dossier.id : null,
      option.consultationType ? option.consultationType.id : null
    )
  }

  handleDropdownSubmit() {
    this.props.resolveProposal(
      this.props.proposal.id,
      this.state.selectedDossierId,
      this.state.selectedConsultationTypeId
    )
  }

  renderQuickOptions() {
    const options = this.buildQuickOptions()

    if (options.length > MAX_QUICK_OPTIONS || options.every(o => !o.dossier)) {
      return this.renderDropdowns()
    }

    if (this.state.showDropdowns) {
      return this.renderDropdowns()
    }

    return (
      <div>
        {options.map((option, i) => (
          <Button key={i} onClick={() => this.handleQuickOption(option)}>
            {this.formatOptionLabel(option)}
          </Button>
        ))}
        <Button onClick={() => this.setState({ showDropdowns: true })}>
          {I18n.t('components.match_proposal_list_item.other')}
        </Button>
      </div>
    )
  }

  renderDropdowns() {
    const { allDossiers, allConsultationTypes } = this.props

    const dossierOptions = (allDossiers || []).map((d) => ({
      key: d.id,
      value: d.id,
      text: d.patient ? `${d.patient.firstName} ${d.patient.lastName}` : `Dossier ${d.id}`
    }))

    const ctOptions = (allConsultationTypes || []).map((ct) => ({
      key: ct.id,
      value: ct.id,
      text: ct.name
    }))

    return (
      <div style={{ paddingTop: 10 }}>
        <Dropdown
          placeholder={I18n.t('components.match_proposal_list_item.select_patient')}
          search
          selection
          options={dossierOptions}
          value={this.state.selectedDossierId}
          onChange={(e, { value }) => this.setState({ selectedDossierId: value })}
          style={{ marginRight: 5 }}
        />
        <Dropdown
          placeholder={I18n.t('components.match_proposal_list_item.select_type')}
          search
          selection
          options={ctOptions}
          value={this.state.selectedConsultationTypeId}
          onChange={(e, { value }) => this.setState({ selectedConsultationTypeId: value })}
          style={{ marginRight: 5 }}
        />
        {this.state.selectedDossierId && (
          <Button icon="checkmark" color="green" onClick={() => this.handleDropdownSubmit()} />
        )}
      </div>
    )
  }

  ambiguityType() {
    const { candidateDossiers, candidateConsultationTypes } = this.props
    const dossierAmbiguous = candidateDossiers.length !== 1
    const ctAmbiguous = candidateConsultationTypes.length > 1

    if (dossierAmbiguous && ctAmbiguous) return 'multiple_both'
    if (dossierAmbiguous) return 'multiple_dossiers'
    if (ctAmbiguous) return 'multiple_consultation_types'
    return 'no_match'
  }

  render() {
    const { event } = this.props
    const ambiguity = this.ambiguityType()

    const titleKey = `components.notifications_list_item.titles.match_proposal.${ambiguity}`
    const title = event
      ? `${I18n.t(titleKey)} ${event.summary}`
      : I18n.t(titleKey)

    const description = I18n.t(`components.match_proposal_list_item.descriptions.${ambiguity}`)

    return (
      <CollapsableCard title={title}>
        <Card.Content key="proposal_content">
          <Card.Meta>
            {event && event.startTime ? I18n.l('time.formats.long', event.startTime) : null}
          </Card.Meta>
          <Card.Description>{description}</Card.Description>

          <div style={{ paddingTop: 10 }}>
            {this.renderQuickOptions()}
          </div>
        </Card.Content>
        <Card.Content key="proposal_not_patient">
          <div style={{ paddingTop: 10 }}>
            <Button color="red" floated="right" onClick={() => this.props.ignoreProposal(this.props.proposal.id)}>
              {I18n.t('components.match_proposal_list_item.not_a_consultation')}
            </Button>
          </div>
        </Card.Content>
      </CollapsableCard>
    )
  }
}

const mapStateToProps = (state, ownProps) => {
  const proposal = ownProps.proposal
  const event = findEntity(state, 'event', proposal.eventId)

  const candidateDossiers = (proposal.dossierCandidateIds || [])
    .map((id) => findEntity(state, 'dossier', id))
    .filter(Boolean)

  const candidateConsultationTypes = (proposal.consultationTypeCandidateIds || [])
    .map((id) => findEntity(state, 'consultationType', id))
    .filter(Boolean)

  const allDossiers = getPersistedEntities('dossier')(state) || []
  const allConsultationTypes = getPersistedEntities('consultationType')(state) || []

  return {
    proposal,
    event,
    candidateDossiers,
    candidateConsultationTypes,
    allDossiers,
    allConsultationTypes
  }
}

const mapDispatchToProps = (dispatch) => {
  return {
    loadDossiers: () => {
      dispatch(fetchEntities('dossiers', { name: 'matchProposal', page: 1, append: true }))
    },
    resolveProposal: (proposalId, dossierId, consultationTypeId) => {
      const attrs = { matchProposalId: proposalId }
      if (dossierId) attrs.chosenDossierId = dossierId
      if (consultationTypeId) attrs.chosenConsultationTypeId = consultationTypeId
      const reviewId = dispatch(createEntity('matchReview', attrs))
      dispatch(upsertEntity('matchReview', reviewId))
    },
    ignoreProposal: (proposalId) => {
      const reviewId = dispatch(createEntity('matchReview', { matchProposalId: proposalId }))
      dispatch(upsertEntity('matchReview', reviewId))
    }
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(MatchProposalListItem)
